import { useMemo, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { trpc } from '@/config/trpc'
import { type DayOfWeek } from '@backend/lib/db/schema/automation'
import { TimezonePickerDialog } from '@/components/automations/timezone-picker-dialog'
import { AutomationsHeader } from '@/components/automations/automations-header'
import { AutomationCard } from '@/components/automations/automation-card'
import { AutomationsEmptyState } from '@/components/automations/automations-empty-state'
import { AutomationsSkeleton } from '@/components/automations/automations-skeleton'
import { AutomationDialog, type AutomationDraft } from '@/components/automations/automation-dialog'
import { getTodayInTimezone } from '@/components/automations/schedule'
import {
  dayRowMap,
  groupAutomations,
  groupKey,
  type AutomationGroup,
} from '@/components/automations/grouping'

type DialogState = { mode: 'create'; day: DayOfWeek } | { mode: 'edit'; group: AutomationGroup }

function toastError(message: string, error: unknown) {
  toast.error(message, {
    description: error instanceof Error ? error.message : String(error),
  })
}

export function AutomationsPage() {
  const utils = trpc.useUtils()
  const { data: moodboards, isLoading: moodboardsLoading } = trpc.moodboard.getAll.useQuery()
  const { data: automations, isLoading: automationsLoading } = trpc.automation.list.useQuery()
  const { data: brand } = trpc.brand.getCurrent.useQuery()

  const { mutateAsync: createAutomation, isPending: isCreating } =
    trpc.automation.create.useMutation()
  const { mutateAsync: updateAutomation, isPending: isUpdating } =
    trpc.automation.update.useMutation()
  const { mutateAsync: deleteAutomation } = trpc.automation.delete.useMutation()
  const { mutateAsync: updateBrand, isPending: isSavingTimezone } = trpc.brand.update.useMutation()

  // Plain update for the group Active/Paused toggle. The optimistic cache write
  // happens ONCE for the whole group in handleToggleActive so the group flips
  // atomically — per-row onMutate would land the flips across separate
  // microtasks, momentarily splitting the group into an active + a paused card.
  const { mutateAsync: updateActive } = trpc.automation.update.useMutation()

  const [timezoneOpen, setTimezoneOpen] = useState(false)
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const [lastCreatedGroupKey, setLastCreatedGroupKey] = useState<string | null>(null)

  const groups = useMemo(() => groupAutomations(automations ?? []), [automations])

  // Only an analyzed moodboard can drive generation, so only `ready` ones count
  // as usable sources.
  const readyMoodboards = useMemo(
    () => (moodboards ?? []).filter((m) => m.status === 'ready'),
    [moodboards],
  )

  const isLoading = moodboardsLoading || automationsLoading
  const canAdd = readyMoodboards.length > 0
  const hasAnyMoodboard = (moodboards?.length ?? 0) > 0
  const groupCount = groups.length
  // Fall back to local time until the brand loads — avoids a flash of the
  // wrong day while `brand.timezone` is still in flight.
  const today = useMemo(() => getTodayInTimezone(brand?.timezone), [brand?.timezone])

  const openCreate = () => {
    if (!canAdd) {
      toast.error('Set up and analyze a moodboard before adding automations.')
      return
    }
    setDialog({ mode: 'create', day: today })
  }

  const handleSubmit = async (draft: AutomationDraft) => {
    const editing = dialog?.mode === 'edit' ? dialog.group : null
    const source = { moodboardId: draft.moodboardId }
    try {
      if (editing) {
        await reconcileGroup(editing, draft, source)
      } else {
        await Promise.all(
          draft.daysOfWeek.map((day) =>
            createAutomation({
              ...source,
              dayOfWeek: day,
              platforms: draft.platforms,
              sendReminders: true,
            }),
          ),
        )
        // Key of the group these new rows will form (all created active).
        setLastCreatedGroupKey(
          groupKey({
            moodboardId: source.moodboardId,
            platforms: draft.platforms,
            sendReminders: true,
          }),
        )
      }
      await utils.automation.list.invalidate()
      setDialog(null)
    } catch (error) {
      toastError(editing ? 'Could not save automation' : 'Could not create automation', error)
    }
  }

  /**
   * Reconcile a group's existing single-day rows to the draft's (source,
   * platforms, days) using the single-day endpoints:
   *  - target day with existing row(s) → update the first, delete extras
   *  - target day with no row → create it
   *  - existing day dropped from targets → delete all its rows
   */
  const reconcileGroup = async (
    group: AutomationGroup,
    draft: AutomationDraft,
    source: { moodboardId: number },
  ) => {
    const existing = dayRowMap(group)
    const targetDays = new Set(draft.daysOfWeek)
    const ops: Promise<unknown>[] = []

    for (const day of draft.daysOfWeek) {
      const rows = existing.get(day)
      if (rows && rows.length > 0) {
        // Update the first row to the new source + platforms.
        ops.push(
          updateAutomation({
            id: rows[0]!.id,
            ...source,
            dayOfWeek: day,
            platforms: draft.platforms,
          }),
        )
        // Clean up any duplicate rows that shared this day.
        for (const dup of rows.slice(1)) ops.push(deleteAutomation({ id: dup.id }))
      } else {
        ops.push(
          createAutomation({
            ...source,
            dayOfWeek: day,
            platforms: draft.platforms,
            sendReminders: group.sendReminders,
          }),
        )
      }
    }

    // Days removed from the group → delete all their rows.
    for (const [day, rows] of existing) {
      if (targetDays.has(day)) continue
      for (const row of rows) ops.push(deleteAutomation({ id: row.id }))
    }

    await Promise.all(ops)
  }

  const handleToggleActive = async (group: AutomationGroup, active: boolean) => {
    const ids = new Set(group.rows.map((r) => r.id))
    await utils.automation.list.cancel()
    const previous = utils.automation.list.getData()
    // Flip every member row in a single setData so the group transitions
    // atomically from its active key to its paused key (no mixed-state split).
    utils.automation.list.setData(undefined, (old) =>
      old?.map((a) => (ids.has(a.id) ? { ...a, sendReminders: active } : a)),
    )
    try {
      await Promise.all(group.rows.map((r) => updateActive({ id: r.id, sendReminders: active })))
    } catch (error) {
      utils.automation.list.setData(undefined, previous)
      toastError('Could not update automation', error)
    } finally {
      utils.automation.list.invalidate()
    }
  }

  const handleDelete = async (group: AutomationGroup) => {
    try {
      await Promise.all(group.rows.map((row) => deleteAutomation({ id: row.id })))
      await utils.automation.list.invalidate()
    } catch (error) {
      toastError('Could not delete automation', error)
    }
  }

  const handleTimezoneSelect = async (timezone: string) => {
    try {
      await updateBrand({ timezone })
      await utils.brand.getCurrent.invalidate()
      setTimezoneOpen(false)
      toast.success('Timezone updated')
    } catch (error) {
      toastError('Could not update timezone', error)
    }
  }

  let content: ReactNode = null
  if (isLoading) {
    content = <AutomationsSkeleton />
  } else if (!canAdd) {
    content = <AutomationsEmptyState variant="no-sources" hasAnyMoodboard={hasAnyMoodboard} />
  } else if (groupCount === 0) {
    content = <AutomationsEmptyState variant="first-run" onNewAutomation={openCreate} />
  } else {
    content = (
      <>
        <p className="text-muted-foreground mt-1 text-xs">
          {groupCount} {groupCount === 1 ? 'automation' : 'automations'} · Runs at 8:00 in{' '}
          {brand?.timezone ?? 'UTC'} ·{' '}
          <button
            type="button"
            onClick={() => setTimezoneOpen(true)}
            disabled={!brand}
            className="text-primary hover:underline disabled:opacity-50">
            change
          </button>
        </p>

        <div className="mt-6 flex flex-col gap-3">
          {groups.map((group) => (
            <AutomationCard
              key={group.key}
              group={group}
              moodboards={moodboards ?? []}
              timezone={brand?.timezone}
              isNew={group.key === lastCreatedGroupKey}
              onEdit={() => setDialog({ mode: 'edit', group })}
              onDelete={() => handleDelete(group)}
              onToggleActive={(active) => handleToggleActive(group, active)}
            />
          ))}
        </div>
      </>
    )
  }

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 pb-24 pt-6 sm:px-6 md:pt-8">
      <AutomationsHeader disabledNew={!canAdd} onNewAutomation={openCreate} />

      {content}

      {dialog && moodboards && (
        <AutomationDialog
          key={dialog.mode === 'edit' ? `edit-${dialog.group.key}` : `create-${dialog.day}`}
          open
          onOpenChange={(o) => {
            if (!o) setDialog(null)
          }}
          moodboards={moodboards}
          group={dialog.mode === 'edit' ? dialog.group : undefined}
          defaultDay={dialog.mode === 'edit' ? (dialog.group.days[0] ?? today) : dialog.day}
          isSaving={isCreating || isUpdating}
          onSubmit={handleSubmit}
          timezone={brand?.timezone ?? 'UTC'}
        />
      )}

      <TimezonePickerDialog
        open={timezoneOpen}
        onOpenChange={setTimezoneOpen}
        current={brand?.timezone ?? 'UTC'}
        onSelect={handleTimezoneSelect}
        isSaving={isSavingTimezone}
      />
    </section>
  )
}
